import { cn, tv } from '@pcln/horizon'
import { type VariantProps } from 'tailwind-variants'
import { MULTI_DESTINATION, ROUND_TRIP } from '../constants'

// Flight type variant definitions
const flightTypeVariants = {
  multiDestination: {
    gridContainer: 'sm:grid-cols-3',
    flightTypeWrapper: 'sm:[grid-area:unset]'
  },
  oneWay: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'submit_submit_submit_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  oneWayNoCabin: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'submit_submit_submit_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTrip: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_passengers_passengers_cabin_cabin'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  },
  roundTripNoCabin: {
    gridContainer: [
      'sm:grid-cols-6 lg:grid-cols-[1fr_1fr_1fr_1fr_max-content]',
      "lg:[grid-template-areas:'options_options_options_options_options'_'origin_destination_timeline_passengers_submit'_'deals_deals_deals_deals_deals']",
      "sm:[grid-template-areas:'options_options_options_options_options_options'_'origin_origin_origin_destination_destination_destination'_'timeline_timeline_timeline_passengers_passengers_passengers'_'deals_deals_deals_submit_submit_submit']"
    ],
    flightTypeWrapper: 'sm:[grid-area:options]'
  }
}

const isMultiDestinationVariants = {
  true: {
    passengersInput: 'sm:[grid-area:unset]',
    cabinInput: 'sm:[grid-area:unset]'
  },
  false: {
    passengersInput: 'sm:[grid-area:passengers]',
    cabinInput: 'sm:[grid-area:cabin]'
  }
}

export type FlightTypeVariant = keyof typeof flightTypeVariants
// export type IsMultiDestinationVariant = keyof typeof isMultiDestinationVariants

const flightFormSlots = {
  gridContainer: 'grid grid-cols-1 gap-3 items-center max-w-6xl mx-auto',
  flightTypeWrapper: [
    'text-nowrap lg:[grid-area:auto/1/auto/-1] w-max grid gap-4',
    'grid-cols-[max-content_max-content] items-center [grid-area:unset]'
  ],
  inputBase: '[grid-area:unset] **:translate-y-0',
  originInput: '[grid-area:unset] sm:[grid-area:origin]',
  destinationInput: '[grid-area:unset] sm:[grid-area:destination]',
  timelineInput: '[grid-area:unset] sm:[grid-area:timeline] **:translate-y-0',
  passengersInput: '[grid-area:unset] **:translate-y-0',
  cabinInput: 'lg:hidden [grid-area:unset]'
}

const flightFormDefaultVariants = {
  flightType: 'oneWay' as const,
  isMultiDestination: false
}

export const horizonFlightForm = tv({
  slots: flightFormSlots,
  variants: {
    flightType: flightTypeVariants,
    isMultiDestination: isMultiDestinationVariants
  },
  defaultVariants: flightFormDefaultVariants
})

export type HorizonFlightFormVariants = VariantProps<typeof horizonFlightForm>

// Helper function to determine flight type variant
const getFlightTypeVariant = (
  flightType: string,
  hideCabin: boolean
): FlightTypeVariant => {
  if (flightType === MULTI_DESTINATION) {
    return 'multiDestination'
  }

  if (flightType === ROUND_TRIP) {
    return hideCabin ? 'roundTripNoCabin' : 'roundTrip'
  }

  return hideCabin ? 'oneWayNoCabin' : 'oneWay'
}

// Utility functions
export const getGridContainerClass = (
  flightType: string,
  addHotel: boolean,
  hideCabin = false
) => {
  const flightTypeVariant = getFlightTypeVariant(flightType, hideCabin)
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    flightType: flightTypeVariant,
    isMultiDestination
  })

  return cn(
    styles.gridContainer(),
    addHotel ? 'with-hotel' : 'without-hotel',
    flightType.toLowerCase()
  )
}

export const getFlightTypeClass = (flightType: string) => {
  const flightTypeVariant = getFlightTypeVariant(flightType, false)
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    flightType: flightTypeVariant,
    isMultiDestination
  })

  return styles.flightTypeWrapper()
}

export const getInputClass = (
  type: 'origin' | 'destination' | 'timeline' | 'passengers' | 'cabin',
  flightType = 'OW'
) => {
  const isMultiDestination = flightType === MULTI_DESTINATION

  const styles = horizonFlightForm({
    isMultiDestination
  })

  const inputMap = {
    origin: styles.originInput,
    destination: styles.destinationInput,
    timeline: styles.timelineInput,
    passengers: styles.passengersInput,
    cabin: styles.cabinInput
  } as const

  return inputMap[type]()
}
